import {
    CreatePluginType,
    CreateOptionsType,
    OptionsHandlerType,
    EmblaCarouselType,
    EmblaEventType
} from 'embla-carousel'

// Declare module augmentation for TypeScript
declare module 'embla-carousel' {
    interface EmblaPluginsType {
        parallax: ParallaxType
    }
}

// Plugin options type
export type OptionsType = CreateOptionsType<{
    amount: number
}>

// Default options
export const defaultOptions: OptionsType = {
    active: true,
    breakpoints: {},
    amount: 0.2
}

// Plugin type definition
export type ParallaxType = CreatePluginType<{}, OptionsType>
export type ParallaxOptionsType = ParallaxType['options']

function Parallax(userOptions: ParallaxOptionsType = {
    amount: 0.2
}): ParallaxType {
    let options: OptionsType
    let emblaApi: EmblaCarouselType
    let tweenNodes: HTMLElement[] = []
    let tweenFactor = 0

    function init(
        emblaApiInstance: EmblaCarouselType,
        optionsHandler: OptionsHandlerType
    ): void {
        emblaApi = emblaApiInstance
        
        const { mergeOptions, optionsAtMedia } = optionsHandler
        const optionsBase = mergeOptions(defaultOptions, Parallax.globalOptions)
        const allOptions = mergeOptions(optionsBase, userOptions)
        options = optionsAtMedia(allOptions)

        setTweenNodes()
        setTweenFactor()
        tweenParallax()

        emblaApi
            .on('reInit', setTweenNodes)
            .on('reInit', setTweenFactor)
            .on('reInit', tweenParallax)
            .on('scroll', tweenParallax)
            .on('slideFocus', tweenParallax)
    }

    function destroy(): void {
        emblaApi
            .off('reInit', setTweenNodes)
            .off('reInit', setTweenFactor)
            .off('reInit', tweenParallax)
            .off('scroll', tweenParallax)
            .off('slideFocus', tweenParallax)
    }

    function setTweenNodes(): void {
        tweenNodes = emblaApi.slideNodes().map((slideNode) => {
            return slideNode.childNodes[0] as HTMLElement
        })
    }

    function setTweenFactor(): void {
        tweenFactor = options.amount * emblaApi.scrollSnapList().length
    }

    function tweenParallax(eventName?: EmblaEventType): void {
        const engine = emblaApi.internalEngine()
        const scrollProgress = emblaApi.scrollProgress()
        const slidesInView = emblaApi.slidesInView()
        const isScrollEvent = eventName === 'scroll'

        emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
            let diffToTarget = scrollSnap - scrollProgress
            const slidesInSnap = engine.slideRegistry[snapIndex]

            slidesInSnap.forEach((slideIndex) => {
                if (isScrollEvent && !slidesInView.includes(slideIndex)) return

                if (engine.options.loop) {
                    engine.slideLooper.loopPoints.forEach((loopItem) => {
                        const target = loopItem.target()

                        if (slideIndex === loopItem.index && target !== 0) {
                            const sign = Math.sign(target)

                            if (sign === -1) {
                                diffToTarget = scrollSnap - (1 + scrollProgress)
                            }
                            if (sign === 1) {
                                diffToTarget = scrollSnap + (1 - scrollProgress)
                            }
                        }
                    })
                }

                const translate = diffToTarget * (-1 * tweenFactor) * 100
                const tweenNode = tweenNodes[slideIndex]?.nextSibling
                tweenNode.style.transform = `translate3d(${translate}%, 0, 0)`
            })
        })
    }

    const self: ParallaxType = {
        name: 'parallax',
        options: userOptions,
        init,
        destroy
    }
    return self
}

// Add global options support
declare namespace Parallax {
    let globalOptions: ParallaxOptionsType | undefined
}

Parallax.globalOptions = undefined

export default Parallax